<template>
  <div class="mpc position-relative">
    <div class="blur position-fixed">
    </div>
    <b-row>
      <b-col md="6">
        <div class="bread">
          <span @click="goTop">HOME</span>
          /
          <span @click="goExplore">MODEL PREDICTIVE CONTROL</span>
          /
          <strong>MPC TECHNOLOGY</strong>
        </div>
        <h1>Model Predictive Control</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col md="6">
        <div class="buttons">
          <b-button pill variant="outline-primary" :to="{ name: 'Benefits' }">
            BENEFITS BY AREA
          </b-button>
          <b-button pill :pressed="true" variant="outline-primary" @click="goHome">
            MPC TECHNOLOGY
          </b-button>
        </div>
      </b-col>
    </b-row>
    <div id="mpcMap" v-if="currentTab === null">
      <b-row>
        <b-col>
          <p>Click the diagram to learn more</p>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="8" offset="2">
          <div class="mpc-links diagram position-relative">
            <img class="diagram" src="@/assets/img/mpc/cement_mpc_home.png" />
            <div class="position-absolute" id="modelingLink" @click="tab('modeling')">
            </div>
            <div class="position-absolute" id="analyzerLink" @click="tab('analyzer')">
            </div>
            <div class="position-absolute" id="controllerLink" @click="tab('controller')">
            </div>
            <div class="position-absolute" id="consoleLink" @click="tab('console')">
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
    <b-row v-if="currentTab !== null">
      <b-col>
        <div class="position-relative" id="mpcTabs">
          <div class="mpc-tab" id="modeling" v-if="currentTab === 'modeling'">
            <b-row class="largeChart">
              <b-col md="8">
                <h2>MODELING / IDENTIFICATION AND ANALYSIS</h2>
                <p>Hybrid modeling functionality allows the user to take advantage of all of the known information (empirical data, equations, equipment specifications, etc).</p>
              </b-col>
            </b-row>
            <b-row v-if="!showExample">
              <b-col md="3">
                <b-row>
                  <b-col class="text-center"><h3>MODEL CREATION</h3></b-col>
                </b-row>
                <b-row class="model-labels">
                  <b-col md="6">
                    <h4>DATA</h4>
                  </b-col>
                  <b-col md="5">
                    <h4 class="model-link text-md-right modelMatrix" @click="showModel">
                     <span style="white-space: nowrap"> MATRIX MODEL
                      <font-awesome-icon :icon="['far', 'angle-double-right']" /></span>
                    </h4>
                  </b-col>
                </b-row>
                 <div class="model-lottie">
                  <img class="diagram" src="@/assets/img/mpc/Kiln-Calendar-Graphic.gif" />
                </div>
              </b-col>
              <b-col class="divider d-xs-none d-md-block" md="1">
                <img src="@/assets/img/mpc/yellow-line-vertical.svg" />
              </b-col>
              <b-col md="4">
                <h4 class="cvs">Controlled variables (CVs)</h4>
                <p>Process variables to maintain at a target or within a range (can be considered outputs)</p>
                <h4 class="ccvs">Constrain variable (CCVs)</h4>
                <p>The state is forbidden to penetrate or may have physical limitations</p>
                <h4 class="mvs">Manipulated variables (MVs)</h4>
                <p>A manipulated input is one that can be adjusted by the control system (or process operator)</p>
                <h4 class="dvs">Disturbance variables (DVs)</h4>
                <p>Disturbance variables - these are also called "load" variables and represent input variables that can cause the controlled variables to deviate</p>
              </b-col>
              <b-col md="4">
                <img class="diagram" src="@/assets/img/mpc/cement_mpc_id@2x.png" />
              </b-col>
            </b-row>
            <b-row v-if="showExample">
              <b-col md="4">
                <img class="diagram" src="@/assets/img/mpc/cement-modeling-id.png" />
                <p><strong>Model Matrix</strong> shows dynamic relationships between process outputs and inputs.</p>
                <p>The system predicts future values of the outputs by movement of all the inputs
(manipulated variables and disturbance variables).</p>
              </b-col>
              <b-col md="7" class="matrix model-link" @click="hideModel">
                <img class="diagram" src="@/assets/img/mpc/illustrative-kiln-matrix.svg" />
              </b-col>
            </b-row>
          </div>
          <div class="mpc-tab" id="analyzer" v-if="currentTab === 'analyzer'">
            <b-row>
              <b-col md="6">
                <h2>VIRTUAL ONLINE ANALYZER</h2>
                <p>Virtual Online Analyzers replace sparse, or infrequent, feedback from lab or analyzer.</p>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <img class="diagram" src="@/assets/img/mpc/Cement-Kiln-Graphic.gif" />
              </b-col>
              <b-col md="6">
                <b-row>
                  <b-col cols="2">
                    <img class="filter" src="@/assets/img/mpc/filter-icon.svg" />
                  </b-col>
                  <b-col cols="2" offset="2">
                    <img class="diagram" src="@/assets/img/mpc/data-qualification-icon.svg" />
                    <h5 class="text-center">
                    DATA QUALIFICATION &amp; SENSOR VALIDATION
                    </h5>
                  </b-col>
                </b-row>
                <b-row class="yellow-arrows">
                  <b-col cols="5">
                    <img class="diagram" src="@/assets/img/mpc/dots-arrow-icon-yellow.svg" />
                  </b-col>
                  <b-col cols="5">
                    <img class="diagram" src="@/assets/img/mpc/dots-arrow-icon-yellow.svg" />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">
                    <h3>INPUTS</h3>
                    <ul>
                      <li>Fuel flows</li>
                      <li>Fuel quality</li>
                      <li>Air flow</li>
                      <li>Process O2</li>
                      <li>Temperatures</li>
                      <li>Ambient</li>
                      <li>Humidity</li>
                    </ul>
                  </b-col>
                  <b-col cols="2">
                    <img class="diagram" src="@/assets/img/mpc/process-furnace-icon.svg" />
                  </b-col>
                  <b-col cols="5" offset="1">
                    <h3>OUTPUTS</h3>
                    <ul>
                      <li>NO X</li>
                      <li>O2</li>
                      <li>CO</li>
                    </ul>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
          <div class="mpc-tab" id="controller" v-if="currentTab === 'controller'">
            <b-row>
              <b-col md="8">
                <h2>CONTROLLER/OPTIMIZER</h2>
              </b-col>
               <b-col class="wrapText" md="10">
                <p>MPC uses a model of the process to predict how the process output variables will respond to changes in the process input variables and disturbances.</p>
                <p>MPC allows for process stability while delivering peak performance since it finds optimal tradeoffs operating closer to system specifications limit whilst managing the process within constraints and safety margins.</p>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="4">
                <img class="diagram consoleImages" src="@/assets/img/mpc/cement_optimizer-diagram.png" />
              </b-col>
              <b-col md="6" offset="1">
                <img class="diagram consoleImages" src="@/assets/img/mpc/cement-optLimit@2x.png" />
              </b-col>
            </b-row>
          </div>
          <div class="mpc-tab" id="console" v-if="currentTab === 'console'">
            <b-row>
              <b-col md="8">
                <h2>CONSOLE</h2>
              </b-col>
              <b-col class="" md="10">
                <p>Built-in controller KPIs provide easy to understand views of control utilization, time at constraints, model error, and other key metrics to determine effectiveness of the control.</p>
              </b-col>
            </b-row>
            <b-row>
              <b-col md="6">
                <img class="diagram consoleImages" src="@/assets/img/mpc/console.gif" />
                <p>Additional metrics for production, quality, energy usage, and other factors can be easily configured to provide continuous measurements of the benefits derived from the application.</p>
              </b-col>
              <b-col md="5">
                <h5>EXAMPLES</h5>
                <hr style="height:1px; border:none; color:#f8c214; background-color:#f8c214; text-align:center;">
                <b-row>
                  <b-col class="screenCenter">
                    <img class="diagram consoleImages" src="@/assets/img/mpc/variation-example@2x.png" />
                    <p class="text-center">Variation</p>
                  </b-col>
                  <b-col class="screenCenter">
                    <img class="diagram consoleImages" src="@/assets/img/mpc/process-trends-example@2x.png" />
                    <p class="text-center">Process Trends</p>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col class="screenCenter">
                    <img class="diagram consoleImages" src="@/assets/img/mpc/production-rate-example@2x.png" />
                    <p class="text-center">Production Rate</p>
                  </b-col>
                  <b-col class="screenCenter">
                    <img class="diagram consoleImages" src="@/assets/img/mpc/control-utilization-example@2x.png" />
                    <p class="text-center">Control Utilization</p>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row>
      <b-col class="text-right">
        <img src="@/assets/img/RA_Logo_Bug-LeftText_white.svg" />
      </b-col>
    </b-row>
  </div>
</template>

<script>

export default {
  data () {
    return {
      showExample: false,
      currentTab: null
    }
  },
  methods: {
    goExplore () {
      this.$router.push({ name: 'Explore' })
    },
    goHome () {
      this.currentTab = null
    },
    goTop () {
      this.$router.push({ name: 'Home' })
    },
    hideModel () {
      this.showExample = false
    },
    showModel () {
      this.showExample = true
    },
    tab (tab) {
      this.currentTab = tab
    }
  },
  mounted () {
    this.$store.commit('showLogo', false)
  },
  name: 'MPC'
}
</script>

<style lang="scss" scoped>

.screenCenter{text-align:center;}

.mpc {
  background: url('../assets/img/benefits/still.png');
  background-repeat: no-repeat;
  background-size: cover;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  padding-left: 3%;
  padding-top: 1.7%;

  .blur {
    backdrop-filter: blur(10px);
    background: rgba(0, 0, 0, 0.7);
    width: 103%;
    height: 103%;
    top: -3%;
    left: -3%;
  }

  .ccvs {
    color: var(--red);
  }

  .cvs {
    color: var(--yellow);
  }

  .diagram {
    width: 100%;
    padding-bottom:20px;
  }

  .consoleImages {
    width: 80%;
  }

  .dvs {
    color: var(--orange);
  }

  .filter {
    width: 70%;
  }

  .matrix {
    margin-top: -60px;
  }

  .model-labels {
    margin-top: 50px;
  }

  .model-link:hover {
    cursor: pointer;
  }

  .model-lottie {
    margin-top: -20px;
  }

  .mpc-links div:hover {
    cursor: pointer;
  }

  .mvs {
    color: var(--blue);
  }

  .yellow-arrows {
    margin: 20px 0;
  }

  #analyzerLink {
    left: 2%;
    top: 33%;
    width: 45%;
    height: 8%;
  }

  #consoleLink {
    left: 86%;
    top: 22%;
    width: 12%;
    height: 19%;
  }

  #controllerLink {
    left: 51%;
    top: 33%;
    width: 34%;
    height: 8%;
  }

  #modelingLink {
    left: 2%;
    top: 22%;
    width: 83%;
    height: 8%;
  }

  #mpcTabs {
    margin-top: 45px;
  }
  .modelMatrix:hover{color:#F9C20A;}

  .largeChart{padding-bottom:80px;}

  .wrapText{ column-count: 2;
  column-gap: 40px;
  }

}
</style>
